<template>
  <v-autocomplete
    v-model="lazyValue"
    v-validate="rules"
    :items="experts"
    :loading="loading"
    :readonly="isExpertSelectorReadOnly"
    :data-vv-name="dataVvName"
    :append-icon="isExpertSelectorReadOnly ? null : 'mdi-menu-down'"
    item-text="full_name"
    item-value="id"
    outlined
    dense
    hide-details
    class="LKMunMehExpertSelector b-autocomplete"
    @change="onExpertChanged"
  >
    <template #prepend>
      <v-tooltip bottom>
        <template #activator="{ on }">
          <v-icon
            x-small
            :color="color"
            style="padding: 8px 0;"
            v-on="on"
            v-text="'mdi-circle'"
          />
        </template>
        <span v-text="statusHint" />
      </v-tooltip>
    </template>
    <template #item="{ item }">
      <v-list-item-content>
        <v-list-item-title :class="{ 'red--text': item.unknown }" v-text="item.full_name" />
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script>
import { lkMapper } from '@/store/modules/lk'
import { isNumber } from 'lodash'
import { MunMehFormStatuses } from '@/typings/api/MunMeh'
import { isBeforeToday } from '@/helpers/dateFormatter'

export default {
  name: 'LKMunMehExpertSelector',
  $_veeValidate: {
    /** @type 'new' */
    validator: 'new',
  },
  props: {
    value: {
      type: Number,
      default: null,
    },
    expertIndex: {
      type: Number,
      validator: (v) => isNumber(v) && v >= 1 && v <= 3,
    },
    rowIndex: {
      type: Number,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    experts: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
  },
  computed: {
    ...lkMapper.mapGetters(['profile']),
    rules() {
      const { data } = this
      switch (this.expertIndex) {
        case 1:
          return `excluded:${data.first_level_expert_two_id},${data.second_level_expert_id}`
        case 2:
          return `excluded:${data.first_level_expert_one_id},${data.second_level_expert_id}`
        case 3:
          return `excluded:${data.first_level_expert_one_id},${data.first_level_expert_two_id}`
        default:
          return ''
      }
    },
    dataVvName() {
      return `Эксперт ${this.expertIndex}. Строка №${this.rowIndex + 1}.`
    },
    lazyValue: {
      get() {
        return this.value
      },
      set(v) {
        this.$emit('input', v)
      },
    },
    isExpertSelectorReadOnly() {
      return !this.profile.is_superuser && this.data.curator_id !== this.profile.id
    },
    isNotAvailable() {
      return ![
        MunMehFormStatuses.submitted,
        MunMehFormStatuses.first_level_marked,
        MunMehFormStatuses.second_level_marked,
        MunMehFormStatuses.finished,
      ].includes(this.data.status)
    },
    isNotStarted() {
      const { data } = this
      switch (this.expertIndex) {
        case 1:
          return !data.expert_1_started_at
        case 2:
          return !data.expert_2_started_at
        case 3:
          return !data.expert2_1_started_at
        default:
          return false
      }
    },
    isInProgress() {
      const { data } = this
      switch (this.expertIndex) {
        case 1:
          return isBeforeToday(data.expert_1_started_at) && !data.expert_1_ended_at
        case 2:
          return isBeforeToday(data.expert_2_started_at) && !data.expert_2_ended_at
        case 3:
          return isBeforeToday(data.expert2_1_started_at) && !data.expert2_1_ended_at
        default:
          return false
      }
    },
    isFinished() {
      const { data } = this
      switch (this.expertIndex) {
        case 1:
          return isBeforeToday(data.expert_1_ended_at)
        case 2:
          return isBeforeToday(data.expert_2_ended_at)
        case 3:
          return isBeforeToday(data.expert2_1_ended_at)
        default:
          return false
      }
    },
    color() {
      if (this.isNotAvailable) {
        return null
      }

      if (this.isNotStarted) {
        return 'error'
      }

      if (this.isInProgress) {
        return 'success'
      }

      if (this.isFinished) {
        return 'orange'
      }
      return null
    },
    statusHint() {
      if (this.isNotAvailable) {
        return 'Недоступно'
      }

      if (this.isNotStarted) {
        return 'Не присутпал'
      }

      if (this.isInProgress) {
        return 'Взята в работу'
      }

      if (this.isFinished) {
        return 'Завершено'
      }
      return null
    },
  },
  methods: {
    async onExpertChanged() {
      await this.$validator.reset()
      this.$nextTick(async () => {
        await this.$validator.validate(this.dataVvName)
        this.$emit('change:experts', this.data.id, this.$errors)
      })
    },
  },
}
</script>

<style lang="scss">
.LKMunMehExpertSelector {
  .success--text {
    color: $webflow-color-green !important;
  }

  .orange--text {
    color: $webflow-color-orange-2 !important;
  }
}
</style>
